// ── Capacitor Native-Bootstrap ────────────────────────────────────────────
// Läuft NUR in der nativen App (iOS/Android-WebView). Im Web ein No-op
// (früher Guard via isNativePlatform → die @capacitor/@capgo-Module werden im
// Web-Bundle gar nicht geladen, da sie hinter dynamischem import() liegen).
//
// Verantwortlich für drei native-spezifische Dinge:
//   1. OTA: CapacitorUpdater.notifyAppReady() — PFLICHT. Signalisiert Capgo,
//      dass der Start erfolgreich war. Ohne diesen Call rollt Capgo das frisch
//      installierte Bundle beim nächsten Start automatisch zurück (Crash-Schutz).
//   2. StatusBar: helle Icons auf unserem dunklen Grund (#0C0C0F).
//   3. SplashScreen ausblenden, sobald die App gemountet ist.
import { Capacitor } from '@capacitor/core';

export async function initNative() {
  if (!Capacitor.isNativePlatform()) return;

  // 1) OTA als Erstes bestätigen (so früh wie möglich → kein Rollback-Risiko).
  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    await CapacitorUpdater.notifyAppReady();
  } catch (e) {
    console.warn('[native] notifyAppReady failed:', e?.message);
  }

  // 2) StatusBar: Edge-to-Edge — WebView geht UNTER die Statusbar (overlay),
  // Inhalt nutzt den ganzen Screen inkl. Notch-Bereich. Die Safe-Area oben wird
  // im Web-Layout via env(safe-area-inset-top) gepolstert (scroll-Container +
  // schwebende Icons). Schriftfarbe der Statusbar ans aktuelle Theme koppeln:
  // helle App → dunkle Schrift (Style.Light), dunkle App → helle Schrift (Style.Dark).
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setOverlaysWebView({ overlay: true });
    const setStyleForTheme = () => {
      // Theme aus html[data-theme] lesen (ThemeContext spiegelt es dorthin);
      // Fallback localStorage. NICHT document.body-Helligkeit — die bleibt fix
      // hell (#E9E9E7 aus index.css) und liess die Uhrzeit im Dark Mode dunkel.
      // Style.Dark = helle (weisse) Schrift für dunklen Grund; Style.Light =
      // dunkle Schrift für hellen Grund.
      let dark = false;
      try {
        const attr = document.documentElement.getAttribute('data-theme');
        if (attr) dark = attr === 'dark';
        else dark = localStorage.getItem('red_dark_mode') === 'true';
      } catch (_) {}
      StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {});
      if (Capacitor.getPlatform() === 'android') {
        StatusBar.setBackgroundColor({ color: '#00000000' }).catch(() => {});
      }
    };
    setStyleForTheme();
    // Theme-Wechsel mitziehen (ThemeContext setzt html[data-theme]).
    try {
      const obs = new MutationObserver(setStyleForTheme);
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class', 'style'] });
    } catch (_) {}
  } catch (e) { /* StatusBar optional */ }

  // 3) Splash erst ausblenden, wenn React gemountet ist (kein weißer Blitz).
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) { /* Splash optional */ }

  // 4) Keyboard: EIN globaler Modus reichte nie für alle Kontexte (Sheet darf
  //    NICHT mitwandern, Eingabe-unten MUSS steigen, Formular soll scrollen).
  //    Darum `resize: 'none'` → die WebView wird NIE verschoben, die Tastatur
  //    überlagert nur den unteren Rand. Wir steuern alles selbst über ZWEI
  //    CSS-Variablen auf <html>:
  //      --kb     = rohe Tastaturhöhe   (für angedockte Leisten / Cap-Position)
  //      --kbpad  = --kb + kleine Luft  (für Inhalts-Padding über der Tastatur)
  //    Verbraucher (PickerSheet-Liste, zentrale Such-Popups, Wizard-Sheet, …)
  //    lesen var(--kbpad). iOS-Motion: `keyboardWillShow` feuert VOR der Animation
  //    und liefert die Höhe → wir setzen die Variable sofort, eine CSS-Transition
  //    mit der iOS-Kurve lässt die Inhalte synchron mitreiten.
  //    Im Web läuft dieser ganze Block nicht (initNative kehrt vorher zurück) →
  //    --kbpad bleibt 0 → keinerlei Web-/PWA-Verhaltensänderung.
  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    Keyboard.setResizeMode({ mode: 'none' }).catch(() => {});
    // Native (graue, gerade) iOS-Zubehörleiste AUS — sie wird durch unsere
    // abgerundete `KeyboardCap` (components/KeyboardCap.jsx) ersetzt, die per
    // keyboardWillShow/Hide app-farben mit runder Oberkante über der Tastatur
    // sitzt. Dismiss zusätzlich via Tap-außerhalb (unten). iOS-only.
    Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});
    document.addEventListener('touchstart', (e) => {
      if (!e.target.closest('input, textarea, select, [contenteditable]')) {
        Keyboard.hide().catch(() => {});
      }
    }, { passive: true, capture: true });

    // --kbpad reserviert zusätzlich die Höhe der abgerundeten KeyboardCap (44)
    // + etwas Luft, damit Sheet-Listen/Popups NICHT hinter der Kappe verschwinden.
    const CAP_H = 44;
    const rootStyle = document.documentElement.style;
    const setInsets = (h) => {
      rootStyle.setProperty('--kb', `${Math.max(0, h)}px`);
      rootStyle.setProperty('--kbpad', `${h > 0 ? h + CAP_H + 10 : 0}px`);
    };

    // Formular-/In-Page-Fall (C): das fokussierte Feld über die Tastatur holen.
    // Bei Ionic-Screens via `--padding-bottom` auf <ion-content> (der Ionic-Weg),
    // sonst Padding am nächsten scrollbaren Vorfahren. Portal-Overlays
    // (Sheets/Popups) regeln ihren Platz selbst über var(--kbpad).
    let _kbPad = null;
    const findScrollable = (el) => {
      let n = el?.parentElement;
      while (n && n !== document.body) {
        const ov = getComputedStyle(n).overflowY;
        if (/(auto|scroll)/.test(ov) && n.scrollHeight > n.clientHeight + 4) return n;
        n = n.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    };
    const scrollActiveIntoView = () => {
      const el = document.activeElement;
      if (el && el.matches?.('input, textarea, select, [contenteditable]')) {
        try { el.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch (_) {}
      }
    };
    Keyboard.addListener('keyboardWillShow', (info) => {
      const h = (info && info.keyboardHeight) || 300;
      setInsets(h);
      const el = document.activeElement;
      if (!el || !el.matches?.('input, textarea, select, [contenteditable]')) return;
      // In einem Portal-Overlay (Sheet/Popup)? Dann macht var(--kbpad) den Platz,
      // kein zusätzliches Scroll-Padding nötig.
      const inOverlay = el.closest?.('[data-kb-overlay]');
      if (inOverlay) { setTimeout(scrollActiveIntoView, 60); return; }
      const ionContent = el.closest?.('ion-content');
      if (ionContent) {
        _kbPad = { el: ionContent, ion: true, prev: ionContent.style.getPropertyValue('--padding-bottom') };
        ionContent.style.setProperty('--padding-bottom', `${h + 24}px`);
      } else {
        const sc = findScrollable(el);
        const cur = parseInt(getComputedStyle(sc).paddingBottom, 10) || 0;
        _kbPad = { el: sc, ion: false, prev: sc.style.paddingBottom, prevTrans: sc.style.transition };
        sc.style.transition = 'padding-bottom 280ms cubic-bezier(0.38,0.7,0.125,1)';
        sc.style.paddingBottom = `${cur + h + 24}px`;
      }
      setTimeout(scrollActiveIntoView, 60);
      setTimeout(scrollActiveIntoView, 220);
    });
    Keyboard.addListener('keyboardWillHide', () => {
      setInsets(0);
      if (!_kbPad) return;
      const pad = _kbPad;
      if (pad.ion) {
        pad.el.style.setProperty('--padding-bottom', pad.prev || '');
      } else {
        pad.el.style.paddingBottom = pad.prev || '';
        setTimeout(() => { try { pad.el.style.transition = pad.prevTrans || ''; } catch (_) {} }, 300);
      }
      _kbPad = null;
    });
    // Fallback, falls keyboardWillShow je nach iOS/Screen nicht feuert.
    document.addEventListener('focusin', (e) => {
      const el = e.target;
      if (el && el.matches?.('input, textarea, select, [contenteditable]')) {
        setTimeout(() => { try { el.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch (_) {} }, 320);
      }
    }, true);
  } catch (e) { /* Keyboard optional */ }

  // 5) iOS WKWebView: Auto-Zoom beim Fokussieren eines <input> unterbinden.
  //    Symptom: beim Öffnen der Tastatur (Profilname-Edit, Betrag im TX-Editor)
  //    „springt"/zoomt der ganze Screen rein. Das passiert auch bei 18px-Feldern,
  //    also ist NICHT die Font-Größe der Auslöser, sondern dass der Viewport
  //    User-Scaling erlaubt. maximum-scale=1 + user-scalable=no wird im nativen
  //    WKWebView respektiert (anders als im Mobile-Safari). NUR nativ setzen —
  //    die Web-/PWA-index.html behält Pinch-Zoom (Accessibility).
  try {
    const vp = document.querySelector('meta[name="viewport"]');
    if (vp) {
      vp.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
      );
    }
  } catch (_) { /* Viewport optional */ }
}
